import { Router } from "@angular/router";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { TranslocoService } from "@jsverse/transloco";
import { BehaviorSubject, Subscription } from "rxjs";
import { injectQueryParam } from "src/app/support/inject";
import { sntNavigate } from "src/app/support/router";
import { sntTranslate } from "src/app/support/translate";

export function bindAuthStepTitles(
  translocoService: TranslocoService,
  title$: BehaviorSubject<string>,
  subTitle$: BehaviorSubject<string>,
  resolve: () => { title: string; subTitle: string }
): Subscription {
  const sync = (): void => {
    const { title, subTitle } = resolve();
    title$.next(title);
    subTitle$.next(subTitle);
  };

  sync();

  return translocoService.langChanges$.subscribe(() => sync());
}

export async function navigateToReturnUrl(
  input: { returnUrl: string; email: string },
  providers: { router: Router }
): Promise<void> {
  const url = new URL(`${location.origin}${input.returnUrl}`);

  await sntNavigate(providers.router, [url.pathname], {
    queryParams: {
      ...Object.fromEntries(url.searchParams.entries()),
      email: input.email
    }
  });
}

export function injectReturnUrl(): string {
  return injectQueryParam("returnUrl")
    ? decodeURIComponent(injectQueryParam("returnUrl")!)
    : "/";
}

export const AUTH_FLASH_ERROR_KEY = "auth-flash-error";
export const AUTH_FLASH_INFO_KEY = "auth-flash-info";

type CognitoLikeError = {
  code?: string;
  message?: string;
};

export function getCognitoErrorCode(error: unknown): string | undefined {
  if (error === null || error === undefined || typeof error !== "object") {
    return undefined;
  }

  const candidate: CognitoLikeError = error;

  if (typeof candidate.code === "string") {
    return candidate.code;
  }

  return undefined;
}

export function setAuthFlashError(message: string): void {
  sessionStorage.setItem(AUTH_FLASH_ERROR_KEY, message);
}

export function consumeAuthFlashError(): string | undefined {
  const message: string | null = sessionStorage.getItem(AUTH_FLASH_ERROR_KEY);

  if (message) {
    sessionStorage.removeItem(AUTH_FLASH_ERROR_KEY);

    return message;
  }

  return undefined;
}

export function setAuthFlashInfo(message: string): void {
  sessionStorage.setItem(AUTH_FLASH_INFO_KEY, message);
}

export function consumeAuthFlashInfo(): string | undefined {
  const message: string | null = sessionStorage.getItem(AUTH_FLASH_INFO_KEY);

  if (message) {
    sessionStorage.removeItem(AUTH_FLASH_INFO_KEY);

    return message;
  }

  return undefined;
}

export function resolveAuthErrorMessage(
  error: unknown,
  fallbackMessage: string
): string {
  const code: string | undefined = getCognitoErrorCode(error);

  if (code) {
    return getErrorMessageByCode(code, fallbackMessage);
  }

  return fallbackMessage;
}

export function getErrorMessageByCode(code: string, message?: string): string {
  switch (code) {
    case "AliasExistsException": {
      return sntTranslate(
        marker("Questo indirizzo email è già associato a un altro account.")
      );
    }
    case "CodeDeliveryFailureException": {
      return sntTranslate(
        marker("C'è stato un errore nell'invio del codice. Riprova.")
      );
    }
    case "CodeMismatchException": {
      return sntTranslate(marker("Il codice inserito è errato."));
    }
    case "ExpiredCodeException": {
      return sntTranslate(
        marker("Il codice inserito è scaduto, richiedine uno nuovo.")
      );
    }
    case "InternalErrorException": {
      return sntTranslate(
        marker("Si è verificato un errore interno. Riprova più tardi.")
      );
    }
    case "InvalidParameterException": {
      return sntTranslate(
        marker("Dati non validi. Controlla i campi inseriti.")
      );
    }
    case "InvalidPasswordException": {
      return sntTranslate(
        marker("La password non rispetta i requisiti di sicurezza.")
      );
    }
    case "LimitExceededException": {
      return sntTranslate(
        marker(
          "Hai richiesto un nuovo codice troppe volte in un breve periodo di tempo. Attendi un po' e riprova."
        )
      );
    }
    case "NetworkError": {
      return sntTranslate(
        marker(
          "Impossibile contattare il server. Verifica la connessione e riprova."
        )
      );
    }
    case "NotAuthorizedException": {
      return sntTranslate(marker("Username e/o password errati."));
    }
    case "PasswordResetRequiredException": {
      return sntTranslate(
        marker("Devi reimpostare la password prima di accedere.")
      );
    }
    case "ResourceNotFoundException": {
      return sntTranslate(
        marker("Servizio non disponibile. Riprova più tardi.")
      );
    }
    case "TooManyFailedAttemptsException": {
      return sntTranslate(
        marker(
          "Hai effettuato troppi tentativo in un breve periodo di tempo. Aspetta un po' e riprova."
        )
      );
    }
    case "TooManyRequestsException": {
      return sntTranslate(
        marker(
          "Troppe richieste in un breve periodo di tempo. Attendi un po' e riprova."
        )
      );
    }
    case "UserNotConfirmedException": {
      return sntTranslate(
        marker("Devi confermare il tuo indirizzo email prima di accedere.")
      );
    }
    case "UserNotFoundException": {
      return sntTranslate(
        marker("Nessun account trovato con questo indirizzo email.")
      );
    }
    case "UsernameExistsException": {
      return sntTranslate(marker("L'username scelto non è disponibile"));
    }
    default: {
      return message || sntTranslate(marker("Errore"));
    }
  }
}

export const AUTH_PRIVACY_URL = "https://snoots.pet/en/snoots-privacypolicy/";

export const AUTH_TERMS_URL = "https://helpcenter.snoots.pet/";

/** Brief pause so success copy is readable before navigation. */
export async function authSuccessDelay(): Promise<void> {
  await new Promise((res) => setTimeout(res, 400));
}

export function checkPasswordComplexity(password: string): boolean {
  const minLength = 8;

  if (password.length < minLength) {
    return false;
  }

  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasDigit = /\d/.test(password);
  const hasOnlyAllowedCharacters = /^[\x21-\x7E]+$/.test(password);

  return hasLowercase && hasUppercase && hasDigit && hasOnlyAllowedCharacters;
}
